import React, { useState, useEffect, useContext, useRef } from "react";
import { gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";

const DEVICE_SET_ATTRIBUTE = gql`
    mutation DeviceSetAttribute(
        $deviceId: String!
        $name: String!
        $value: JSON
        $clear: Boolean
    ) {
        deviceSetAttribute(
            device_id: $deviceId
            name: $name
            value: $value
            clear: $clear
        )
    }
`;

const AUTO_SAVE_DEBOUNCE_MS = 600;

// Mirrors kAttributes[]'s Settable enum in firmware (main/include/app_attributes.h)
// and the DEVICE_ONLY_ATTRIBUTES lists in backend-v2 / AWS that reject backend
// writes to these keys. Anything not listed here is settable from here (either
// backend-only or two-way — both are editable from this UI).
const DEVICE_ONLY_ATTRIBUTES = ["display_on", "current_job_name", "current_job_code"];

function isReadOnlyFromBackend(name) {
    return DEVICE_ONLY_ATTRIBUTES.includes(name);
}

function inferAttributeType(value) {
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    return "string";
}

// Live attribute editor - values save automatically as they're changed, no
// submit button. Reads device.device.reportedAttributes/overriddenAttributes
// (see ShadowDeviceFieldResolver.ts - reported is the device's last-known
// values, overridden is what the backend currently has set) the same way
// runtime.attributes does, just kept as the raw split here since knowing
// which attributes have an active override vs are just reported is exactly
// what this editor needs to distinguish.
function DeviceAttributesPanel({ device }) {
    const starekContext = useContext(StarekContext);
    const [deviceSetAttribute] = useMutation(DEVICE_SET_ATTRIBUTE, {
        client: starekContext.engineClient,
    });

    const [attributeRows, setAttributeRows] = useState([]);
    const debounceTimers = useRef({});

    useEffect(() => {
        const reported = device?.device?.reportedAttributes || {};
        const overridden = device?.device?.overriddenAttributes || {};
        // Three-tier: device override > assigned profile's own default >
        // device-reported (see ShadowDeviceFieldResolver.ts's
        // applicableAttributes, which computes runtime.attributes with this
        // same precedence) - mirrored here rather than read off
        // runtime.attributes directly since this editor needs the raw split
        // to know which attributes have an active override at all.
        const profileDefaults = device?.device?.profileAttributeDefaults || {};
        setAttributeRows(
            Object.keys(reported).map((name) => {
                // Whether to show/enable the reset addon is a fact about the
                // override itself (does one exist for this attribute), not
                // a value comparison - an override that happens to equal
                // the reported value is still an override, and resetting
                // means actually clearing it (see resetAttribute), not just
                // writing the reported value back as a new override.
                const isOverridden = name in overridden;
                const hasProfileDefault = name in profileDefaults;
                const fallbackValue = hasProfileDefault
                    ? profileDefaults[name]
                    : reported[name];
                const initialValue = isOverridden ? overridden[name] : fallbackValue;
                return {
                    name,
                    type: inferAttributeType(initialValue),
                    value: initialValue,
                    defaultValue: fallbackValue,
                    hasProfileDefault,
                    isOverridden,
                    status: "idle", // idle | saving | saved | error
                };
            }),
        );
    }, [device]);

    useEffect(() => {
        const timers = debounceTimers.current;
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, []);

    const setRowStatus = (name, status) => {
        setAttributeRows((prev) =>
            prev.map((row) => (row.name === name ? { ...row, status } : row)),
        );
    };

    const persistAttribute = async (name, value) => {
        setRowStatus(name, "saving");
        try {
            await deviceSetAttribute({
                variables: { deviceId: device.id, name, value },
            });
            setRowStatus(name, "saved");
            setTimeout(() => setRowStatus(name, "idle"), 1500);
        } catch (err) {
            setRowStatus(name, "error");
            starekContext.showError(err?.message || err);
        }
    };

    const updateAttributeValue = (name, value, immediate = false) => {
        setAttributeRows((prev) =>
            prev.map((row) => (row.name === name ? { ...row, value } : row)),
        );

        if (debounceTimers.current[name]) {
            clearTimeout(debounceTimers.current[name]);
        }

        if (immediate) {
            persistAttribute(name, value);
        } else {
            debounceTimers.current[name] = setTimeout(() => {
                persistAttribute(name, value);
            }, AUTO_SAVE_DEBOUNCE_MS);
        }
    };

    // Reverts to the device's own reported value by clearing the override
    // outright (clear: true - see applyAttributeOverride on the backend),
    // not by writing the reported value back as a new override. Same
    // precedence everywhere else already relies on: an override that
    // happens to equal what's reported is still an override, and would
    // stop tracking the device's live reported value going forward if left
    // in place as one.
    const resetAttribute = async (row) => {
        if (!row.isOverridden) return;
        setRowStatus(row.name, "saving");
        try {
            await deviceSetAttribute({
                variables: { deviceId: device.id, name: row.name, clear: true },
            });
            setAttributeRows((prev) =>
                prev.map((r) =>
                    r.name === row.name
                        ? {
                              ...r,
                              value: r.defaultValue,
                              isOverridden: false,
                              status: "saved",
                          }
                        : r,
                ),
            );
            setTimeout(() => setRowStatus(row.name, "idle"), 1500);
        } catch (err) {
            setRowStatus(row.name, "error");
            starekContext.showError(err?.message || err);
        }
    };

    const renderRow = (row, readOnly) => {
        const resetButton = !readOnly && (
            <button
                type="button"
                disabled={!row.isOverridden || row.status === "saving"}
                onClick={() => resetAttribute(row)}
                title={
                    row.isOverridden
                        ? `Reset to ${row.hasProfileDefault ? "profile" : "reported"} value (${row.defaultValue})`
                        : `Already at the ${row.hasProfileDefault ? "profile" : "reported"} value`
                }
                className={`btn btn-sm ${
                    row.isOverridden ? "btn-warning" : "btn-outline-warning"
                }`}
                style={{ opacity: row.isOverridden ? 1 : 0.5 }}
            >
                <i className="fa fa-undo" />
            </button>
        );

        return (
            <div key={row.name} className="tw-mb-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <span className="tw-text-xs tw-font-medium tw-text-gray-600 tw-truncate">
                        {row.name}
                    </span>
                    <span className="tw-text-[10px] tw-flex-shrink-0 tw-ml-2">
                        {row.status === "saving" && (
                            <span className="tw-text-gray-400">Saving…</span>
                        )}
                        {row.status === "saved" && (
                            <span className="tw-text-green-600">Saved</span>
                        )}
                        {row.status === "error" && (
                            <span className="tw-text-red-600">Failed</span>
                        )}
                    </span>
                </div>
                {row.type === "boolean" ? (
                    <div className="tw-flex tw-items-center">
                        <button
                            disabled={readOnly}
                            onClick={() =>
                                !readOnly &&
                                updateAttributeValue(row.name, !row.value, true)
                            }
                            className={`tw-w-10 tw-h-5 tw-rounded-full tw-relative ${
                                row.value ? "tw-bg-green-500" : "tw-bg-gray-300"
                            } ${readOnly ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
                        >
                            <span
                                className={`tw-absolute tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-top-0.5 tw-transition-transform ${
                                    row.value ? "tw-right-0.5" : "tw-left-0.5"
                                }`}
                            />
                        </button>
                        <span className="tw-ms-auto">{resetButton}</span>
                    </div>
                ) : (
                    <div className="tw-flex tw-items-stretch tw-gap-1">
                        <input
                            type={row.type === "number" ? "number" : "text"}
                            value={row.value}
                            readOnly={readOnly}
                            onChange={(e) =>
                                !readOnly &&
                                updateAttributeValue(
                                    row.name,
                                    row.type === "number"
                                        ? Number(e.target.value)
                                        : e.target.value,
                                )
                            }
                            className={`tw-flex-1 tw-min-w-0 tw-px-2 tw-py-1.5 tw-border tw-rounded tw-text-sm ${
                                readOnly ? "tw-bg-gray-50 tw-text-gray-500" : ""
                            }`}
                        />
                        {resetButton}
                    </div>
                )}
            </div>
        );
    };

    const settableRows = attributeRows.filter(
        (row) => !isReadOnlyFromBackend(row.name),
    );
    const readOnlyRows = attributeRows.filter((row) =>
        isReadOnlyFromBackend(row.name),
    );

    return (
        <div className="tw-p-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                Values save automatically as you edit them.
            </p>
            {attributeRows.length === 0 && (
                <p className="tw-text-sm tw-text-gray-400">
                    No attributes reported yet.
                </p>
            )}

            {settableRows.map((row) => renderRow(row, false))}

            {readOnlyRows.length > 0 && (
                <>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-mb-3">
                        <span className="tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500">
                            Read only
                        </span>
                        <span className="tw-flex-1 tw-border-t tw-border-gray-200" />
                    </div>
                    {readOnlyRows.map((row) => renderRow(row, true))}
                </>
            )}
        </div>
    );
}

export default DeviceAttributesPanel;
